// The room contract. A room is a data package: one model, its baked light
// atlases and this manifest. The Studio core and presenter read only what is
// declared here, so any package that satisfies the contract can stand in for
// the Workshop. Axes are three.js: X right, Y up, Z toward the viewer; metres.

export type Vec3 = [number, number, number];

/** A point on a surface and the direction something placed there faces. */
export interface SurfaceAnchor {
  position: Vec3;
  /** Rotation about Y in radians; 0 faces +Z. */
  yaw: number;
}

/**
 * Where a device rests. In a `cradle` the position is the inner corner of the
 * stand's ledge: the device's bottom back edge sits there and it leans back
 * by `tilt` radians. On a `surface` the position is a point the device lies
 * on, open and face up. The stand itself belongs to the room and stays put.
 */
export interface DeviceAnchor extends SurfaceAnchor {
  support: "cradle" | "surface";
  tilt: number;
}

/** A camera pose and the rectangle at its target that must stay in view. */
export interface CameraPose {
  position: Vec3;
  target: Vec3;
  /** Width and height in metres, measured at the target. */
  fit: [number, number];
}

export type CameraName = "overview" | "devices" | "apps" | "desk" | "laptop" | "tv" | "play" | "build";
export const CAMERA_NAMES: readonly CameraName[] = ["overview", "devices", "apps", "desk", "laptop", "tv", "play", "build"];

export interface RoomLight {
  type: "spot" | "point" | "area";
  position: Vec3;
  direction?: Vec3;
  /** Linear RGB, 0..1. */
  color: Vec3;
  /** Radiant power the bake used, in watts. */
  watts: number;
  /** Full cone angle in radians, spots only. */
  angle?: number;
}

export interface RoomGlow {
  position: Vec3;
  color: string;
  /** Sprite diameter in metres. */
  size: number;
}

export interface RoomManifest {
  room: 1;
  id: string;
  title: string;
  /** GLB with one mesh per atlas; the mesh's material name is the atlas key. */
  model: string;
  atlases: Record<string, string>;
  /** Devices are drawn at this multiple of their real size. */
  deviceScale: number;
  bounds: { min: Vec3; max: Vec3 };
  anchors: {
    /** One per shelf device, in DEVICE_IDS order, each of a kind that device can rest on. */
    deviceSlots: DeviceAnchor[];
    /** Where game cases stand. The shelf shows at most this many apps. */
    appSlots: SurfaceAnchor[];
    /** Where a device stands while it is played. Every device must be able to rest on it. */
    playSpot: DeviceAnchor;
    /** Where a device sits while a game is made for it. Every device must be able to rest on it. */
    buildSpot: DeviceAnchor;
    /** The laptop's display: corners bottom-left, bottom-right, top-right, top-left. */
    laptopScreen: { corners: [Vec3, Vec3, Vec3, Vec3]; resolution: [number, number] };
    /**
     * The television's picture tube, corners in the same order. `resolution`
     * is the picture's logical size and `bulge` how far the glass stands out
     * at its centre, in metres.
     */
    tvScreen: { corners: [Vec3, Vec3, Vec3, Vec3]; resolution: [number, number]; bulge: number };
    /** Where each coding agent is drawn in the room, if the room shows them. Keys are agent ids. */
    agentSpots?: Record<string, SurfaceAnchor>;
  };
  cameras: Record<CameraName, CameraPose>;
  /**
   * How far the visitor may move the overview camera, relative to its pose:
   * azimuth and elevation in radians around the target, and the range of the
   * zoom factor (below 1 is closer).
   */
  orbit: { azimuth: [number, number]; elevation: [number, number]; zoom: [number, number] };
  /**
   * The key light: the one parallel light whose shadows live objects cast.
   * `direction` is the way the light travels and `angle` its angular
   * diameter in radians, which sets how fast its shadows soften with
   * distance. The bake holds its static shadows; `shade` holds, per atlas,
   * the factor a texel keeps when a live object blocks the key from it.
   */
  key: { direction: Vec3; angle: number };
  shade: Record<string, string>;
  /** The lights the bake used, for lighting the live objects to match. */
  lights: RoomLight[];
  /** Soft halos drawn over lamps, bulbs and neon. */
  glows: RoomGlow[];
}

export type Support = DeviceAnchor["support"];

/**
 * Throws with every problem found, so a broken package fails at load.
 * `rests` lists, per device in shelf order, the supports that device accepts.
 */
export function validateRoomManifest(input: unknown, rests: ReadonlyArray<ReadonlyArray<Support>>): RoomManifest {
  const problems: string[] = [];
  const m = input as Partial<RoomManifest> | null;
  if (!m || typeof m !== "object") throw new Error("room manifest is not an object");
  if (m.room !== 1) problems.push("room must be 1");
  for (const key of ["id", "title", "model"] as const) {
    if (typeof m[key] !== "string" || !m[key]) problems.push(`${key} must be a non-empty string`);
  }
  const atlases = Object.keys(m.atlases ?? {});
  if (atlases.length === 0) problems.push("atlases must name at least one texture");
  for (const name of atlases) {
    if (typeof m.shade?.[name] !== "string") problems.push(`shade.${name} missing: every atlas needs its shade texture`);
  }
  if (m.key?.direction?.length !== 3 || Math.abs(Math.hypot(...m.key.direction) - 1) > 1e-3) {
    problems.push("key.direction must be a unit vector");
  }
  if (typeof m.key?.angle !== "number" || !(m.key.angle > 0 && m.key.angle < 1)) {
    problems.push("key.angle must be the key's angular diameter in radians");
  }
  if (typeof m.deviceScale !== "number" || !(m.deviceScale > 0)) problems.push("deviceScale must be positive");
  const a = m.anchors;
  if (!a) problems.push("anchors missing");
  else {
    if (!Array.isArray(a.deviceSlots) || a.deviceSlots.length < rests.length) {
      problems.push(`anchors.deviceSlots needs ${rests.length} slots`);
    } else {
      rests.forEach((accepted, index) => {
        if (!accepted.includes(a.deviceSlots[index]!.support)) {
          problems.push(`anchors.deviceSlots[${index}] is a ${a.deviceSlots[index]!.support}, which that device cannot rest on`);
        }
      });
    }
    if (!Array.isArray(a.appSlots) || a.appSlots.length === 0) problems.push("anchors.appSlots is empty");
    for (const name of ["playSpot", "buildSpot"] as const) {
      const spot = a[name];
      if (!spot) problems.push(`anchors.${name} missing`);
      else if (rests.some((accepted) => !accepted.includes(spot.support))) {
        problems.push(`anchors.${name} is a ${spot.support}, which not every device can rest on`);
      }
    }
    if (!a.laptopScreen || a.laptopScreen.corners?.length !== 4) problems.push("anchors.laptopScreen needs four corners");
    if (!a.tvScreen || a.tvScreen.corners?.length !== 4 || !(a.tvScreen.bulge >= 0)) problems.push("anchors.tvScreen needs four corners and a bulge");
  }
  for (const name of CAMERA_NAMES) {
    const c = m.cameras?.[name];
    if (!c || c.position?.length !== 3 || c.target?.length !== 3 || c.fit?.length !== 2) {
      problems.push(`cameras.${name} missing or malformed`);
    }
  }
  const o = m.orbit;
  if (!o || [o.azimuth, o.elevation, o.zoom].some((range) => !Array.isArray(range) || range.length !== 2 || !(range[0] <= range[1]))) {
    problems.push("orbit needs azimuth, elevation and zoom ranges");
  }
  if (problems.length) throw new Error(`room manifest invalid: ${problems.join("; ")}`);
  return m as RoomManifest;
}
